import React, { useContext, useState } from 'react'
import { EPCRContext } from '../EPCR'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import BigModal from '../../../../components/Modals/BigModal'
import { Cancel, CheckCircle } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import WoundClosure from '../Treatment/Modules/WoundClosure/WoundClosure'
import MedicineSelector from '../Treatment/Modules/MedicineSelector/MedicineSelector'
import moment from 'moment'
import { isHiddenEntryKey } from '../liveCapture/liveUi'

const FastTrackTreatment = () => {
  const { formValues, handleChange, formID } = useContext(EPCRContext)
  const { epcrPracticeMode } = useContext(SiteModeContext)
  const [selectedModule, setSelectedModule] = useState({})

  const submitModule = (moduleName, moduleValues) => {
    handleChange({
      moduleName: 'treatment',
      field: 'modules',
      value: [
        ...(formValues?.treatment?.modules ?? []),
        { ...moduleValues, moduleName },
      ],
    })
    setSelectedModule({})
  }

  const treatmentItems = [
    {
      name: 'Wound Closure',
      module: <WoundClosure submitModule={submitModule} />,
    },
    {
      name: 'Medicines',
      module: (
        <MedicineSelector
          submitModule={submitModule}
          epcrID={formID}
          epcrPracticeMode={epcrPracticeMode}
        />
      ),
      color: 'anMainBlue',
    },
  ]

  const moduleList =
    formValues?.treatment?.modules ??
    [].sort((a, b) => moment(b.dateTime) - moment(a.dateTime))

  return (
    <EPCRCard>
      <Typography variant="title2">Treatment</Typography>
      <Grid container spacing={1}>
        {treatmentItems.map((x) => (
          <Grid item xs={6} md={4} lg={3} xl={2} key={x.name}>
            <Button
              disabled={!x?.module}
              onClick={() => setSelectedModule(x)}
              variant={
                moduleList?.some((m) => m.moduleName === x.name)
                  ? 'contained'
                  : 'outlined'
              }
              size="small"
              color={x?.color ?? 'primary'}
              fullWidth
            >
              {x.name}
            </Button>
          </Grid>
        ))}
        <Grid item xs={12}>
          <SimpleTextField
            value={formValues?.treatment?.treatmentText}
            onChange={(e) =>
              handleChange({
                moduleName: 'treatment',
                field: 'treatmentText',
                value: e.target.value,
              })
            }
            label="Treatment Given"
            multiline
            minRows={2}
          />
        </Grid>
      </Grid>
      {moduleList.map((module, index) => (
        <Grid
          container
          border="solid 1px"
          borderColor="anBlue.main"
          borderRadius={1}
          padding={1}
          key={module?.dateTime ?? index}
          rowGap={1}
        >
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Box display="flex">
                <Typography variant="title3">{module.moduleName}</Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                flex={1}
                alignItems="center"
                gap={1}
              >
                <Typography variant="button">
                  {moment(module.dateTime).format('HH:mm')}
                </Typography>
                {module.moduleName !== 'Medicine Administration' && (
                  <DeleteIcon
                    color="error"
                    onClick={() =>
                      handleChange({
                        moduleName: 'treatment',
                        field: 'modules',
                        value: moduleList.filter((x, idx) => index !== idx),
                      })
                    }
                    sx={{ width: '1.5rem', height: '1.5rem' }}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            flexWrap="wrap"
            columnGap={5}
            rowGap={1}
          >
            {Object.keys(module)
              .filter((x) =>
                isHiddenEntryKey(x, ['dateTime', 'moduleName', 'Notes'])
                  ? false
                  : true
              )
              .map((x) => (
                <Box key={x} display="flex" gap={0.5}>
                  <Typography fontWeight="bold" sx={{ color: '#268EBB' }}>
                    {x}:{' '}
                  </Typography>
                  <Typography variant="body1">
                    {module.isChecklist ? (
                      module[x] ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="error" />
                      )
                    ) : Array.isArray(module[x]) ? (
                      module[x].join(', ')
                    ) : (
                      module[x]
                    )}
                  </Typography>
                </Box>
              ))}
          </Grid>
          {module?.['Notes'] && (
            <Grid item xs={12} lg={6}>
              <Box display="flex" gap={0.5}>
                <Typography fontWeight="bold">Notes: </Typography>
                <Typography variant="body1">{module['Notes']}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      ))}
      <BigModal
        open={Object.keys(selectedModule).length > 0}
        onClose={() => setSelectedModule({})}
      >
        {selectedModule.module}
      </BigModal>
    </EPCRCard>
  )
}

export default FastTrackTreatment
